/* eslint-disable react/prop-types */
/* eslint-disable camelcase */
import axios from 'axios';
import { useFormik } from 'formik';
import { get, isEqual } from 'lodash';
import queryString from 'query-string';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { ButtonGroup, Card, Col, Container, Form, Modal, OverlayTrigger, Row, Spinner, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { connect, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import Alert from '../../../../../javascript/shared/components/Alert';
import OauthButton from '../../../../../javascript/shared/components/OauthButton/OauthButton';
import { SET_USER } from '../../../../actions/auth.actions';
import { addFlashMessage } from '../../../../actions/flashMessages.actions';
import Button from '../../../../shared/components/Button';
import FormControl from '../../../../shared/components/Form/FormControl';
import ResponsiveHeader from '../../../../shared/components/ResponsiveHeader';
import Select from '../../../../shared/components/Select';
import useBreakpoint from '../../../../shared/hooks/useBreakpoint';

const TRANSLATION_PATH = 'influencers.pages.profile.social_platforms';

function SocialPlatform({ user, setUser }) {
  const breakpoint = useBreakpoint();
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();

  const queryParams = queryString.parse(location.search);
  const { youtube_oauth_callback, facebook_oauth_callback } = queryParams;

  const socialPlatforms = ['Instagram', 'TikTok', 'YouTube', 'Twitter', 'Blog', 'Podcast', 'Snapchat', 'LinkedIn', 'Pinterest'];
  const blogPlatforms = [
    'WordPress', 'Wix', 'Blogger', 'Tumblr', 'Medium',
    'Squarespace', 'Joomla', 'Ghost', 'Weebly', 'Other'
  ];

  const [state, setState] = useState({
    inputList: [],
    youtubeConnected: user.yout_oauth_token || youtube_oauth_callback,
    facebookConnected: user.facebook_oauth_token || facebook_oauth_callback,
    showModalDeleteConnection: false
  });

  const attachedSocialPlatforms = socialPlatforms.filter(platform => {
    if (!user.social) return false;

    const platformData = user.social[platform.toLowerCase().slice(0, 4)];

    if (!platformData && !(platform == 'Instagram' && state.facebookConnected) && !(platform == 'YouTube' && state.youtubeConnected)) return false;

    return (!!platformData.nickname ||
      !!platformData.url ||
      !!platformData.rss_link ||
      (platform == 'Instagram' && state.facebookConnected) ||
      (platform == 'YouTube' && state.youtubeConnected));
  });

  useEffect(() => {
    const list = attachedSocialPlatforms;
    if (!isEqual(list, state.inputList)) setState({ ...state, inputList: list });
  }, [user]);

  const handleModalDeleteConnection = useCallback(
    ({ platform, open }) => {
      setState({ ...state, showModalDeleteConnection: open });
    }, [state]);

  const Alerts = useCallback(({ platform }) => {
    let alertIcon, description, btnText, btnIcon, variant, btnSecText, btnSecIcon;

    if (platform === 'Instagram' && !user.facebook_oauth_enabled) return null;

    switch (platform) {
      case 'YouTube':
        if (state.youtubeConnected) {
          alertIcon = 'fa-thumbs-up';
          description = t(`${TRANSLATION_PATH}.youtube_disconnect_prompt`);
          btnText = t(`${TRANSLATION_PATH}.delete_connection`);
          btnIcon = 'fal fa-times';
          variant = '';
        } else {
          alertIcon = 'fa-exclamation-circle';
          description = t(`${TRANSLATION_PATH}.youtube_connect_prompt`);
          btnText = t(`${TRANSLATION_PATH}.connect_to_youtube`);
          btnIcon = 'fab fa-youtube';
          variant = 'success';
        }
        return <Alert className={`mt-0 ${formik.isSubmitting ? 'loading' : ''}`} variant={variant}>
          <div className='d-flex flex-column w-100'>
            <div>
              <i className={`icon fal ${alertIcon}`}></i>
              {description}
            </div>
            <div className='align-self-end'>
              <i className={`align-middle ${btnIcon}`}></i>
              <OauthButton
                link
                platform="google_oauth2"
                afterDisconnect={(data) => {
                  setState({ ...state, youtubeConnected: false, showModalDeleteConnection: false });
                  setUser(data.user);
                  history.replace({ search: '' });
                }}
                disconnect={state.youtubeConnected}
                text={btnText}
              />
            </div>
          </div>
        </Alert>;
      case 'Instagram':
        if (user.fb_hash_error && !state.facebookConnected) {
          alertIcon = 'fa-alert-circle';
          description = t(`${TRANSLATION_PATH}.instagram_error_connection`);
          btnText = t(`${TRANSLATION_PATH}.delete_connection`);
          btnIcon = 'fal fa-times';
          btnSecText = t(`${TRANSLATION_PATH}.reconnect`);
          btnSecIcon = 'fab fa-instagram';
          variant = 'danger';
        } else if (user.fb_hash_error) {
          alertIcon = 'fa-alert-triangle';
          description = t(`${TRANSLATION_PATH}.instagram_about_to_expire`);
          btnText = t(`${TRANSLATION_PATH}.delete_connection`);
          btnIcon = 'fal fa-times';
          btnSecText = t(`${TRANSLATION_PATH}.reconnect`);
          btnSecIcon = 'fab fa-instagram';
          variant = 'warning';
        } else if (state.facebookConnected) {
          alertIcon = 'fa-thumbs-up';
          description = t(`${TRANSLATION_PATH}.instagram_disconnect_prompt`);
          btnText = t(`${TRANSLATION_PATH}.delete_connection`);
          btnIcon = 'fal fa-times';
          btnSecText = t(`${TRANSLATION_PATH}.update_settings`);
          btnSecIcon = 'fab fa-instagram';
          variant = 'success';
        } else {
          alertIcon = 'fa-exclamation-circle';
          description = t(`${TRANSLATION_PATH}.instagram_connect_prompt`);
          btnSecText = t(`${TRANSLATION_PATH}.connect_to_instagram`);
          btnSecIcon = 'fab fa-instagram';
          variant = 'info';
        }

        return <Alert className={`mt-0 ${formik.isSubmitting ? 'loading' : ''}`} variant={variant}>
          <div className='d-flex flex-column w-100'>
            <div>
              <i className={`icon fal ${alertIcon}`}></i>
              {description}
            </div>
            <div className='align-self-end'>
              <Button variant="link" onClick={() => handleModalDeleteConnection({ platform: 'facebook', open: true })}>
                <i className={`align-middle ${btnIcon} mr-2`} />
                {btnText}
              </Button>
              {
                btnSecText ?
                  <OauthButton
                    link
                    platform="facebook"
                    text={
                      <React.Fragment>
                        <i className={`align-middle ${btnSecIcon} mr-2`} />
                        {btnSecText}
                      </React.Fragment>
                    }
                  /> : null
              }
            </div>
          </div>
        </Alert>;

      default:
        return null;
    }
  }, [state]);

  const handleSelectChange = (e, index) => {
    const { value } = e.target;
    const list = [...state.inputList];

    if (!list.includes(value)) list[index] = value;

    setState({ ...state, inputList: list });
  };

  const deleteParams = (platform) => {
    let params = {};

    if (platform == 'Blog') {
      params = { blog_url: null, blog_platform: null };
    } else if (platform == 'Podcast') {
      params = { podcast_rss_link: null, blog_umv: null };
    } else {
      params = { [`${platform.toLowerCase().slice(0, 4)}_username`]: null };
    }

    return params;
  };

  const handleRemoveClick = (platform, index) => {
    const list = [...state.inputList];
    const removedPlatform = list.splice(index, 1);

    formik.setFieldValue(`${platform.toLowerCase()}`, '');

    if (attachedSocialPlatforms.includes(removedPlatform[0])) {
      setState({ loading: true, inputList: list });
      axios.put('/api/v1/influencers/users', {
        user: deleteParams(platform)
      }).then(({ data }) => {
        setState({ loading: false, inputList: list });
        if (data.success) {
          setUser(data.user);
        } else {
          throw 'Something went wrong!';
        }
      }).catch(e => {
        setState({ ...state, loading: false });
      });
    } else {
      setState({ ...state, inputList: list });
    }
  };

  const handleAddClick = () => {
    const availablePlatform = socialPlatforms.find(platform => !state.inputList.includes(platform));
    if (availablePlatform) setState({ ...state, inputList: [...state.inputList, availablePlatform] });
  };

  const params = (values) => {
    let userParams = {
      inst_username: values.instagram,
      yout_username: values.youtube,
      tikt_username: values.tiktok,
      twit_username: values.twitter,
      blog_url: values.blog,
      podcast_rss_link: values.podcast,
      blog_platform: values.blogPlatform,
      blog_umv: values.uniqueMonthlyVisitors,
      snap_username: values.snapchat,
      pint_username: values.pinterest,
      link_username: values.linkedin
    };

    Object.keys(userParams).forEach((k) => {
      ([null, undefined, ''].includes(userParams[k])) && delete userParams[k];
    });

    return userParams;
  };

  const fieldsMap = (value) => {
    return { 
      inst_username: 'instagram',
      yout_username: 'youtube',
      tikt_username: 'tiktok',
      twit_username: 'twitter',
      blog_url: 'blog',
      podcast_rss_link: 'podcast',
      blog_platform: 'blogPlatform',
      blog_umv: 'uniqueMonthlyVisitors',
      snap_username: 'snapchat',
      pint_username: 'pinterest',
      link_username: 'linkedin'
    }[value];
  }

  const validationSchema = Yup.object({
    instagram: state.inputList.includes('Instagram')
      ? Yup.string().required(`${t('influencers.common.forms.is_required')}`)
      : Yup.string().notRequired(),
    youtube: state.inputList.includes('YouTube')
      ? Yup.string().required(`${t('influencers.common.forms.is_required')}`)
      : Yup.string().notRequired(),
    tiktok: state.inputList.includes('TikTok')
      ? Yup.string().required(`${t('influencers.common.forms.is_required')}`)
      : Yup.string().notRequired(),
    twitter: state.inputList.includes('Twitter')
      ? Yup.string().required(`${t('influencers.common.forms.is_required')}`)
      : Yup.string().notRequired(),
    blog: state.inputList.includes('Blog')
      ? Yup.string().required(`${t('influencers.common.forms.is_required')}`)
      : Yup.string().notRequired(),
    podcast: state.inputList.includes('Podcast')
      ? Yup.string().required(`${t('influencers.common.forms.is_required')}`)
      : Yup.string().notRequired(),
    snapchat: state.inputList.includes('Snapchat')
      ? Yup.string().required(`${t('influencers.common.forms.is_required')}`)
      : Yup.string().notRequired(),
    linkedin: state.inputList.includes('LinkedIn')
      ? Yup.string().required(`${t('influencers.common.forms.is_required')}`)
      : Yup.string().notRequired(),
    pinterest: state.inputList.includes('Pinterest')
      ? Yup.string().required(`${t('influencers.common.forms.is_required')}`)
      : Yup.string().notRequired(),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      instagram: get(user, 'social.inst.nickname') || '',
      youtube: get(user, 'social.yout.nickname') || '',
      tiktok: get(user, 'social.tikt.nickname') || '',
      twitter: get(user, 'social.twit.nickname') || '',
      blog: get(user, 'social.blog.url') || '',
      podcast: get(user, 'social.podc.rss_link') || '',
      blogPlatform: get(user, 'social.blog.platform') || blogPlatforms[0],
      uniqueMonthlyVisitors: get(user, 'social.podc.umv') || 0,
      snapchat: get(user, 'social.snap.nickname') || '',
      pinterest: get(user, 'social.pint.nickname') || '',
      linkedin: get(user, 'social.link.nickname') || ''
    },
    validationSchema,
    onSubmit: async(values, { setSubmitting, setErrors }) => {
      try {
        const { data } = await axios.put('/api/v1/influencers/users', {
          user: params(values),
        });
        if (data.success) {
          setUser(data.user);
        } else {
          throw new Error('Something went wrong!');
        }
      } catch (error) {
        if (error.response && error.response.status === 422) {
          console.log(error);
          const serverErrors = {};
          const errors = error.response.data.errors
          Object.keys(errors).forEach(field => {
            serverErrors[fieldsMap(field)] = errors[field];
          });
          setErrors(serverErrors);
        } else {
          dispatch(addFlashMessage({
            type: 'error',
            text: 'Something went wrong. Please try again.',
          }));
        }
      } finally { setSubmitting(false); }
    }
  });

  const displayPlatformLabel = (platform, isTouched, hasError) => {
    let labelAndPlaceholder = { label: '', placeholder: '' };

    switch (platform) {
      case 'Blog':
        labelAndPlaceholder['label'] = `${t(`${TRANSLATION_PATH}.website_url`)}`;
        labelAndPlaceholder['placeholder'] = 'www.blog.com';
        break;

      case 'Podcast':
        labelAndPlaceholder['label'] = `${t(`${TRANSLATION_PATH}.podcast_rss`)}`;
        labelAndPlaceholder['placeholder'] = 'www.rss.com';
        break;

      case "Snapchat":
      case "LinkedIn":
      case "Pinterest":
        labelAndPlaceholder['label'] = `${t(`${TRANSLATION_PATH}.username`)}`;
        labelAndPlaceholder['placeholder'] = `${t(`${TRANSLATION_PATH}.username`)}`;
        break;
      case 'YouTube':
        labelAndPlaceholder['label'] = `${t(`${TRANSLATION_PATH}.channel_name`)}`;
        labelAndPlaceholder['placeholder'] = `${t(`${TRANSLATION_PATH}.channel_name`)}`;
        break;
      default:
        labelAndPlaceholder['label'] = `${t(`${TRANSLATION_PATH}.account_name`)}`;
        labelAndPlaceholder['placeholder'] = `${t(`${TRANSLATION_PATH}.handle_name`)}`;
        break;
    }

    return (
      <Form.Group>
        <Form.Label>
          {labelAndPlaceholder['label']}
          {
            platform === 'YouTube' ? (
              <OverlayTrigger
                placement="bottom"
                overlay={
                  <Tooltip>
                    <div className="text-3 tc-white">
                      {t(`${TRANSLATION_PATH}.youtube_channel_info`)}
                    </div>
                  </Tooltip>
                }
              >
                <i className="fal fa-info-circle ml-2" />
              </OverlayTrigger>
            ) : null
          }
        </Form.Label>
        <FormControl
          type="text"
          placeholder={labelAndPlaceholder['placeholder']}
          {...formik.getFieldProps(`${platform.toLowerCase()}`)}
        />
        <Form.Text className="text-muted danger-text">
          {isTouched && hasError}
        </Form.Text>
      </Form.Group>
    )
  };

  return (<React.Fragment>
    <Form className='mb-6' onSubmit={formik.handleSubmit}>
      <Container className="mt-6">
        <ResponsiveHeader
          xs='h4'
          sm='h3'
          text={t(`${TRANSLATION_PATH}.title`)}
          className='mb-5'
        />

        {state.inputList.length ?
          <Fragment>
            {state.inputList.map((platform, i) => {
              const isTouched = get(formik, `touched.${platform.toLowerCase()}`, false);
              const hasError = get(formik, `errors.${platform.toLowerCase()}`, false);

              return (
                <Fragment key={i}>
                  <Row id="forms">
                    <Col xs={12}>
                      <Row>
                        <Col xs={12} sm={3}>
                          <Form.Group>
                            <Form.Label>
                              {t(`${TRANSLATION_PATH}.social_platform`)}
                            </Form.Label>
                            <Select
                              className="mb-2"
                              value={platform}
                              name="platform"
                              onChange={e => handleSelectChange(e, i)}
                              options={
                                socialPlatforms
                                  .filter(socialPlatform => !state.inputList.includes(socialPlatform) || platform === socialPlatform)
                                  .map(socialPlatform => ({
                                    label: socialPlatform,
                                    value: socialPlatform
                                  }))
                              }
                            />
                          </Form.Group>
                        </Col>

                        <Col xs={['YouTube', 'TikTok', 'Twitter'].includes(platform) ? 8 : 12} sm={3}>
                          {displayPlatformLabel(platform, isTouched, hasError)}
                        </Col>

                        {platform == 'Instagram' &&
                          <Col xs={8} sm={4}>
                            <Form.Group>
                              <Form.Label>{t(`${TRANSLATION_PATH}.facebook_id`)}</Form.Label>
                              <FormControl type="text" placeholder="23948732948349834" />
                            </Form.Group>
                          </Col>
                        }

                        {platform == 'Podcast' &&
                          <Col xs={8} sm={4}>
                            <Form.Group>
                              <Form.Label>
                                {t(`${TRANSLATION_PATH}.monthly_unique_users`)}
                              </Form.Label>
                              <FormControl
                                type="number"
                                placeholder="0"
                                {...formik.getFieldProps('uniqueMonthlyVisitors')}
                              />
                            </Form.Group>
                          </Col>
                        }

                        {platform == 'Blog' &&
                          <Col xs={8} sm={4}>
                            <Form.Group>
                              <Form.Label>
                                {t(`${TRANSLATION_PATH}.blog_platform`)}
                              </Form.Label>
                              <Select
                                className="mb-2"
                                name="blogPlatform"
                                {...formik.getFieldProps('blogPlatform')}
                                onBlur={() => formik.setFieldTouched('blogPlatform')}
                                options={blogPlatforms.map(blogPlatform => ({
                                  label: blogPlatform,
                                  value: blogPlatform
                                }))}
                              />
                            </Form.Group>
                          </Col>
                        }

                        <Col xs={4} sm={1} className="mb-3" style={{ marginTop: '29px' }}>
                          <div className="btn-gradient">
                            <div className="background-gradient"></div>
                            <button
                              type="button"
                              className="btn btn-secondary px-4"
                              onClick={() => handleRemoveClick(platform, i)}
                            >
                              <i className="fal fa-trash"></i>
                            </button>
                          </div>
                        </Col>
                      </Row>
                      <Alerts platform={platform} />
                    </Col>
                  </Row>
                  {breakpoint == 'xs' && <hr className='mb-4' />}
                </Fragment>
              );
            })}
          </Fragment> :
          <Card className="empty mb-4-5">
            <span>{t(`${TRANSLATION_PATH}.no_social_platforms`)}</span>
            <div>{t(`${TRANSLATION_PATH}.you_wont_be_matched`)}</div>
          </Card>
        }
      </Container>

      <Container>
        {(!!state.inputList.length) && breakpoint != 'xs' && <hr className='mt-6 mb-4' />}
        <Row className='justify-content-end'>
          <Col xs="auto">
            <ButtonGroup>
              <Button
                variant="secondary"
                type="button"
                onClick={handleAddClick}
                disabled={formik.isSubmitting}
              >
                {t(`${TRANSLATION_PATH}.add_social_platform`)}
              </Button>
            </ButtonGroup>
          </Col>
        </Row>
      </Container>

      <hr className='my-6' />

      <Container>
        <Row className='justify-content-end'>
          <Col xs="auto">
            <ButtonGroup>
              <Button
                variant="secondary"
                type="button"
                onClick={_ => {
                  document.querySelector('body').scrollTo({ top: 0, behavior: 'smooth' });
                  formik.resetForm();
                  setState({ ...state, inputList: attachedSocialPlatforms });
                }}
              >
                {t('influencers.common.discard_changes')}
              </Button>
              <div className="btn-gradient align-self-end">
                <div className="background-gradient"></div>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={formik.isSubmitting }
                >
                  {formik.isSubmitting 
                    ? <Spinner animation="border" size="sm" />
                    : t('influencers.common.save_changes')
                  }
                </button>
              </div>
            </ButtonGroup>
          </Col>
        </Row>
      </Container>

    </Form>
    <Modal
      show={state.showModalDeleteConnection}
      onHide={() => handleModalDeleteConnection({ platform: 'facebook', open: false })}
    >
      <Modal.Header closeButton>
        <Modal.Title>{t(`${TRANSLATION_PATH}.delete_connection`)}</Modal.Title>
      </Modal.Header>
      <Modal.Body>{t(`${TRANSLATION_PATH}.delete_connection_desc`)}</Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={() => handleModalDeleteConnection({ platform: 'facebook', open: false })}>
          {t('influencers.common.cancel')}
        </Button>
        <OauthButton
          platform="facebook"
          variant="primary"
          afterDisconnect={(data) => {
            setState({ ...state, facebookConnected: false, showModalDeleteConnection: false });
            setUser(data.user);
            history.replace({ search: '' });
          }}
          disconnect={state.facebookConnected}
          text={t(`${TRANSLATION_PATH}.delete_connection`)}
        />
      </Modal.Footer>
    </Modal>
  </React.Fragment>
  );
}

const mapStateToProps = (state) => ({ user: state.auth.user });
const mapDispatchToProps = (dispatch) => ({
  setUser: (user) => dispatch({ type: SET_USER, payload: user }),
});

export default connect(mapStateToProps, mapDispatchToProps)(SocialPlatform);
